import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Check,
  FolderOpen,
  Images,
  Loader2,
  Monitor,
  Pencil,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import type { CampaignListItem } from '@/features/campaigns/api/campaignApi';
import { useUpdateCampaignContent } from '@/features/campaigns/api/useCampaignMutations';
import {
  useCampaignOverviewQuery,
  type CampaignBillboard,
} from '@/features/campaigns/api/campaignDetailApi';
import { ContentPreviewMedia } from '@/features/campaigns/components/ContentPreviewMedia';
import { GalleryPickerModal } from '@/features/campaigns/components/GalleryPickerModal';
import {
  buildOrientationWarningCopy,
  normalizeOrientations,
} from '@/features/campaigns/components/PreviewStep/previewStepHelpers';
import { getOptimizedImageUrl } from '@/shared/lib/media/optimizeImageUrl';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import {
  firstGalleryMedia,
  galleryItemId,
  isGalleryVideo,
  type GalleryItem,
  type GalleryMediaFile,
} from '@/features/gallery/types/gallery';
import { isVideoContent } from '@/features/campaigns/utils/resolveCreativePreview';

type MediaOrientation = 'PORTRAIT' | 'LANDSCAPE';
type SourceMode = 'gallery' | 'local';

type LocalCreative = {
  mediaId: string;
  orientation: MediaOrientation;
  mediaType: string;
  mediaUrl: string;
  previewBlob: string;
  name?: string;
};

type GallerySelection = {
  galleryId: string;
  previewUrl: string;
  previewType: string;
  name?: string;
};

type EditCampaignContentModalProps = {
  open: boolean;
  campaign: CampaignListItem;
  onClose: () => void;
};

const ORIENTATION_LABEL: Record<MediaOrientation, string> = {
  LANDSCAPE: 'Landscape',
  PORTRAIT: 'Portrait',
};

function mediaTypeOf(media?: GalleryMediaFile | null) {
  return media?.mediaType || media?.type || media?.mimeType || '';
}

function requiredOrientationsFromBillboards(
  billboards: CampaignBillboard[] | undefined,
): MediaOrientation[] {
  if (!billboards?.length) return [];
  const values = billboards.flatMap((b) => {
    const raw = [b.screenOrientation, b.screenType].filter(Boolean) as string[];
    return raw.map((value) => {
      const upper = value.toUpperCase();
      if (upper.includes('VERTICAL') || upper.includes('PORTRAIT'))
        return 'PORTRAIT';
      if (upper.includes('HORIZONTAL') || upper.includes('LANDSCAPE'))
        return 'LANDSCAPE';
      return value;
    });
  });
  return normalizeOrientations(values);
}

export function EditCampaignContentModal({
  open,
  campaign,
  onClose,
}: EditCampaignContentModalProps) {
  const localInputRef = useRef<HTMLInputElement>(null);
  const pendingLocalOrientation = useRef<MediaOrientation>('LANDSCAPE');
  const localByOrientationRef = useRef<Partial<Record<MediaOrientation, LocalCreative>>>(
    {},
  );

  const upload = useUploadSingleFile();
  const deleteFile = useDeleteFile();
  const updateContent = useUpdateCampaignContent();

  const [sourceMode, setSourceMode] = useState<SourceMode>('gallery');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallerySelection, setGallerySelection] = useState<GallerySelection | null>(
    null,
  );
  const [localByOrientation, setLocalByOrientation] = useState<
    Partial<Record<MediaOrientation, LocalCreative>>
  >({});
  const [uploadingOrientation, setUploadingOrientation] =
    useState<MediaOrientation | null>(null);
  const [dismissedWarning, setDismissedWarning] = useState(false);
  const [saving, setSaving] = useState(false);

  const campaignId = String(campaign.campaignId || campaign._id || '');
  const overviewQuery = useCampaignOverviewQuery(open ? campaignId : undefined);
  const overview = overviewQuery.data?.data;

  const requiredOrientations = useMemo(
    () => requiredOrientationsFromBillboards(overview?.billboards),
    [overview?.billboards],
  );

  const orientationOptions = useMemo<MediaOrientation[]>(
    () =>
      requiredOrientations.length > 0
        ? requiredOrientations
        : ['LANDSCAPE', 'PORTRAIT'],
    [requiredOrientations],
  );

  const existingMediaIds = useMemo(
    () =>
      (overview?.mediaIds || [])
        .map((m) => String(m.mediaId || '').trim())
        .filter(Boolean),
    [overview?.mediaIds],
  );

  const coveredOrientations = useMemo(() => {
    if (sourceMode === 'gallery') {
      return gallerySelection ? new Set(orientationOptions) : new Set<string>();
    }
    return new Set(
      Object.values(localByOrientation)
        .map((item) => String(item?.orientation || '').toUpperCase())
        .filter(Boolean),
    );
  }, [sourceMode, gallerySelection, localByOrientation, orientationOptions]);

  const missingOrientations = orientationOptions.filter(
    (o) => !coveredOrientations.has(o),
  );

  const showOrientationWarning =
    sourceMode === 'local' &&
    requiredOrientations.length > 0 &&
    missingOrientations.length > 0 &&
    Object.keys(localByOrientation).length > 0 &&
    !dismissedWarning;

  const orientationResolved =
    sourceMode === 'local' &&
    requiredOrientations.length > 0 &&
    missingOrientations.length === 0;

  const warningCopy = useMemo(
    () =>
      buildOrientationWarningCopy(
        requiredOrientations,
        Object.values(localByOrientation)[0]?.orientation || 'LANDSCAPE',
      ),
    [requiredOrientations, localByOrientation],
  );

  const title = campaign.campaignName || campaign.name || 'Untitled campaign';
  const rawOriginalUrl =
    overview?.mediaIds?.[0]?.mediaUrl || campaign.media?.url || '';
  const originalType =
    overview?.mediaIds?.[0]?.mediaType || campaign.media?.mediaType || '';
  // Weserv is image-only — keep video URLs untouched so <video> can play.
  const originalUrl = isVideoContent(originalType, rawOriginalUrl)
    ? rawOriginalUrl
    : getOptimizedImageUrl(rawOriginalUrl, { width: 640 }) || rawOriginalUrl;

  const hasChange =
    sourceMode === 'gallery'
      ? Boolean(gallerySelection)
      : Object.keys(localByOrientation).length > 0;

  const busy =
    saving ||
    upload.isPending ||
    updateContent.isPending ||
    Boolean(uploadingOrientation);

  const showLoading =
    busy || (open && overviewQuery.isLoading && !overview);

  const loadingLabel =
    saving || updateContent.isPending
      ? 'Saving…'
      : uploadingOrientation || upload.isPending
        ? 'Uploading…'
        : 'Loading…';

  useEffect(() => {
    localByOrientationRef.current = localByOrientation;
  }, [localByOrientation]);

  useEffect(() => {
    if (!open) return;
    setSourceMode('gallery');
    setGalleryOpen(false);
    setGallerySelection(null);
    setLocalByOrientation({});
    setUploadingOrientation(null);
    setDismissedWarning(false);
    setSaving(false);
  }, [open]);

  const deleteLocalCreative = (item?: LocalCreative) => {
    const id = item?.mediaId?.trim();
    if (!id) return;
    void deleteFile.mutateAsync({ mediaId: id }).catch(() => {});
    if (item?.previewBlob?.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewBlob);
    }
  };

  const clearLocalUploads = () => {
    Object.values(localByOrientationRef.current).forEach(deleteLocalCreative);
    setLocalByOrientation({});
  };

  const handleClose = useCallback(() => {
    if (showLoading) return;
    clearLocalUploads();
    onClose();
  }, [showLoading, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showLoading) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, showLoading, handleClose]);

  useEffect(() => {
    return () => {
      Object.values(localByOrientationRef.current).forEach((item) => {
        if (item?.previewBlob?.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewBlob);
        }
      });
    };
  }, []);

  const switchSourceMode = (mode: SourceMode) => {
    if (mode === sourceMode || busy) return;
    if (mode === 'gallery') clearLocalUploads();
    if (mode === 'local') setGallerySelection(null);
    setDismissedWarning(false);
    setSourceMode(mode);
  };

  const handleGallerySelect = (item: GalleryItem) => {
    const media = firstGalleryMedia(item);
    const previewUrl = media?.url || media?.mediaUrl || '';
    const gid = galleryItemId(item);
    if (!gid || !previewUrl) {
      toast.error('Gallery item has no usable media');
      return;
    }
    const previewType = isGalleryVideo(media)
      ? mediaTypeOf(media) || 'video'
      : mediaTypeOf(media) || 'image';
    clearLocalUploads();
    setGallerySelection({
      galleryId: gid,
      previewUrl,
      previewType,
      name: item.contentName,
    });
    setSourceMode('gallery');
    setGalleryOpen(false);
    setDismissedWarning(false);
    toast.success('Gallery creative selected');
  };

  const openLocalUpload = (orientation: MediaOrientation) => {
    pendingLocalOrientation.current = orientation;
    localInputRef.current?.click();
  };

  const handleLocalFile = async (file?: File) => {
    const orientation = pendingLocalOrientation.current;
    if (!file || !orientation || busy) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      toast.error('Please upload an image or video');
      return;
    }
    if (isVideo && file.size > 100 * 1024 * 1024) {
      toast.error('Video must be under 100MB');
      return;
    }
    if (isImage && file.size > 25 * 1024 * 1024) {
      toast.error('Image must be under 25MB');
      return;
    }

    deleteLocalCreative(localByOrientationRef.current[orientation]);
    setGallerySelection(null);
    setSourceMode('local');
    setUploadingOrientation(orientation);
    setDismissedWarning(false);

    const previewBlob = URL.createObjectURL(file);
    const mediaType = file.type || (isVideo ? 'video' : 'image');

    try {
      const result = await upload.mutateAsync({
        file,
        moduleName: 'campaign',
      });
      const mediaId = String(result?.data?.mediaId || '');
      if (!mediaId) throw new Error('Upload failed');
      const durableUrl =
        (result?.data?.url as string | undefined) ||
        (result?.data?.mediaUrl as string | undefined) ||
        '';

      setLocalByOrientation((prev) => ({
        ...prev,
        [orientation]: {
          mediaId,
          orientation,
          mediaType,
          mediaUrl: durableUrl || previewBlob,
          previewBlob,
          name: file.name,
        },
      }));
      toast.success(`${ORIENTATION_LABEL[orientation]} content uploaded`);
    } catch {
      URL.revokeObjectURL(previewBlob);
      toast.error('Upload failed');
    } finally {
      setUploadingOrientation(null);
      if (localInputRef.current) localInputRef.current.value = '';
    }
  };

  const removeLocal = (orientation: MediaOrientation) => {
    deleteLocalCreative(localByOrientation[orientation]);
    setLocalByOrientation((prev) => {
      const next = { ...prev };
      delete next[orientation];
      return next;
    });
    setDismissedWarning(false);
  };

  const handleSave = async () => {
    if (!hasChange || busy) return;

    setSaving(true);
    try {
      if (sourceMode === 'gallery' && gallerySelection) {
        await updateContent.mutateAsync({
          campaignId,
          galleryId: gallerySelection.galleryId,
        });
      } else if (sourceMode === 'local') {
        const mediaIds = Object.values(localByOrientation)
          .filter(Boolean)
          .map((item) => ({
            mediaId: item!.mediaId,
            orientation: item!.orientation,
          }));

        if (!mediaIds.length) {
          toast.error('Upload at least one creative');
          return;
        }

        for (const mediaId of existingMediaIds) {
          await deleteFile.mutateAsync({ mediaId }).catch(() => {});
        }

        await updateContent.mutateAsync({
          campaignId,
          mediaIds,
        });
      } else {
        toast.error('Select content to save');
        return;
      }

      setLocalByOrientation({});
      onClose();
    } catch {
      // Error toast handled by mutation hook.
    } finally {
      setSaving(false);
    }
  };

  const previewUrl =
    sourceMode === 'gallery'
      ? gallerySelection?.previewUrl || originalUrl
      : localByOrientation[orientationOptions[0]]?.previewBlob ||
        localByOrientation[orientationOptions[0]]?.mediaUrl ||
        originalUrl;

  const previewType =
    sourceMode === 'gallery'
      ? gallerySelection?.previewType || originalType
      : localByOrientation[orientationOptions[0]]?.mediaType || originalType;

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4">
        <button
          type="button"
          aria-label="Close overlay"
          className="absolute inset-0 border-0 bg-[#0F1B2D]/50 backdrop-blur-[2px] outline-none"
          onClick={() => {
            if (!showLoading) handleClose();
          }}
        />
        <div
          role="dialog"
          aria-modal
          aria-labelledby="edit-content-title"
          className="camp-fade-up relative z-10 flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
        >
          {showLoading ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-[var(--dash-ink)] shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin text-[var(--dash-brand)]" />
                {loadingLabel}
              </div>
            </div>
          ) : null}

          <div
            className="h-1.5 w-full shrink-0"
            style={{ background: 'var(--dash-gradient)' }}
          />

          <div className="flex shrink-0 items-start justify-between gap-3 px-5 py-4">
            <div className="min-w-0 text-left">
              <div className="mb-1 inline-flex items-center gap-1.5 text-[var(--dash-brand)]">
                <Pencil className="h-3.5 w-3.5" />
                <span className="text-[11px] font-bold tracking-wide uppercase">
                  Edit content
                </span>
              </div>
              <h2
                id="edit-content-title"
                className="m-0 truncate text-lg font-semibold text-[var(--dash-ink)]"
              >
                {title}
              </h2>
              <p className="mt-0.5 mb-0 text-xs text-[var(--dash-muted)]">
                Pick from gallery or upload from your device.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={showLoading}
              className="rounded-full p-2 text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)] disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-2">
            <div className="flex items-start gap-2 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)]/60 px-3 py-2.5 text-left">
              <Monitor className="mt-0.5 h-4 w-4 shrink-0 text-[var(--dash-brand)]" />
              <p className="m-0 text-xs text-[var(--dash-muted)]">
                {requiredOrientations.length > 0 ? (
                  <>
                    Screens need{' '}
                    <span className="font-semibold text-[var(--dash-ink)]">
                      {requiredOrientations.join(' & ')}
                    </span>
                    {sourceMode === 'local'
                      ? ' — upload matching files below.'
                      : ' — gallery picks one approved creative.'}
                  </>
                ) : (
                  <>
                    Either{' '}
                    <span className="font-semibold text-[var(--dash-ink)]">
                      landscape
                    </span>{' '}
                    or{' '}
                    <span className="font-semibold text-[var(--dash-ink)]">
                      portrait
                    </span>{' '}
                    content is required
                    {sourceMode === 'local'
                      ? ' — upload at least one below.'
                      : ' — gallery picks one approved creative.'}
                  </>
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 rounded-xl bg-[var(--dash-soft)] p-1">
              <button
                type="button"
                disabled={showLoading}
                onClick={() => switchSourceMode('gallery')}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition disabled:opacity-50 ${
                  sourceMode === 'gallery'
                    ? 'bg-white text-[var(--dash-brand)] shadow-sm'
                    : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                }`}
              >
                <Images className="h-4 w-4" />
                Gallery
              </button>
              <button
                type="button"
                disabled={showLoading}
                onClick={() => switchSourceMode('local')}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition disabled:opacity-50 ${
                  sourceMode === 'local'
                    ? 'bg-white text-[var(--dash-brand)] shadow-sm'
                    : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                }`}
              >
                <FolderOpen className="h-4 w-4" />
                Local file
              </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-[var(--dash-soft)]">
              <div className="relative aspect-video w-full bg-[#0F1B2D]/5">
                {previewUrl ? (
                  <ContentPreviewMedia
                    key={previewUrl}
                    url={previewUrl}
                    type={previewType}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-[var(--dash-muted)]">
                    No creative yet
                  </div>
                )}
              </div>
              {hasChange ? (
                <p className="m-0 border-t border-[var(--dash-line)] px-3 py-2 text-center text-[11px] font-semibold text-[var(--dash-brand)]">
                  New creative ready
                </p>
              ) : null}
            </div>

            {sourceMode === 'gallery' ? (
              <div className="space-y-2">
                <button
                  type="button"
                  disabled={showLoading}
                  onClick={() => setGalleryOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--dash-line)] bg-white px-3 py-3 text-sm font-semibold text-[var(--dash-ink)] transition hover:border-[var(--dash-brand)] hover:bg-[var(--dash-brand-soft)] hover:text-[var(--dash-brand)] disabled:opacity-50"
                >
                  <Images className="h-4 w-4" />
                  {gallerySelection ? 'Change gallery creative' : 'Select from gallery'}
                </button>
                {gallerySelection ? (
                  <p className="m-0 text-center text-xs text-[var(--dash-muted)]">
                    Selected: {gallerySelection.name || 'Gallery creative'}
                  </p>
                ) : (
                  <p className="m-0 text-center text-xs text-[var(--dash-muted)]">
                    One approved gallery item can be selected.
                  </p>
                )}
              </div>
            ) : (
              <div
                className={`grid gap-3 ${
                  orientationOptions.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
                }`}
              >
                {orientationOptions.map((orientation) => {
                  const item = localByOrientation[orientation];

                  return (
                    <div
                      key={orientation}
                      className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-[var(--dash-soft)]/40 p-3"
                    >
                      <p className="mb-2 text-[11px] font-bold tracking-wide text-[var(--dash-muted)] uppercase">
                        {ORIENTATION_LABEL[orientation]}
                      </p>

                      {item ? (
                        <div className="space-y-2">
                          <div className="relative aspect-video overflow-hidden rounded-xl bg-[#0F1B2D]/5">
                            <ContentPreviewMedia
                              url={item.previewBlob || item.mediaUrl}
                              type={item.mediaType}
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <p className="m-0 truncate text-xs font-semibold text-[var(--dash-ink)]">
                              {item.name || `${orientation} file`}
                            </p>
                            <button
                              type="button"
                              disabled={showLoading}
                              onClick={() => removeLocal(orientation)}
                              className="shrink-0 text-[11px] font-semibold text-red-600 hover:underline disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={showLoading}
                          onClick={() => openLocalUpload(orientation)}
                          className="flex min-h-[120px] w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-[var(--dash-line)] bg-white px-3 py-4 text-center text-sm font-semibold text-[var(--dash-ink)] transition hover:border-[var(--dash-brand)] hover:bg-[var(--dash-brand-soft)] hover:text-[var(--dash-brand)] disabled:opacity-50"
                        >
                          <Upload className="h-5 w-5" />
                          <span>Upload {ORIENTATION_LABEL[orientation].toLowerCase()}</span>
                          <span className="text-[11px] font-normal text-[var(--dash-muted)]">
                            Image max 25MB · Video max 100MB
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <input
              ref={localInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => void handleLocalFile(e.target.files?.[0])}
            />

            {orientationResolved ? (
              <div className="flex items-start gap-2 rounded-2xl border border-green-200 bg-green-50/70 px-3.5 py-3 text-left">
                <Check
                  className="mt-0.5 h-4 w-4 shrink-0 text-[var(--dash-success)]"
                  strokeWidth={3}
                />
                <div className="min-w-0">
                  <p className="m-0 text-sm font-semibold text-[var(--dash-ink)]">
                    Matching content ready
                  </p>
                  <p className="mt-1 mb-0 text-xs text-[var(--dash-muted)]">
                    All required orientations are uploaded.
                  </p>
                </div>
              </div>
            ) : null}

            {showOrientationWarning ? (
              <section className="rounded-2xl border border-red-200 bg-red-50 px-3.5 py-3.5 text-left">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-sm font-semibold text-red-900">
                      {warningCopy.title}
                    </p>
                    <p className="mt-1.5 mb-0 text-xs leading-relaxed text-red-800/90">
                      {warningCopy.body.replace(
                        /AI can convert it for you, but quality may be affected\. For best results, /i,
                        'For best results, ',
                      )}{' '}
                      You can still save with what you have uploaded.
                    </p>
                    <button
                      type="button"
                      disabled={showLoading}
                      onClick={() => setDismissedWarning(true)}
                      className="mt-3 inline-flex items-center justify-center rounded-xl border border-red-300 bg-white px-3.5 py-2.5 text-xs font-semibold text-red-800 transition hover:bg-red-50 disabled:opacity-50"
                    >
                      Continue anyway
                    </button>
                  </div>
                </div>
              </section>
            ) : null}
          </div>

          <div className="flex shrink-0 gap-2.5 border-t border-[var(--dash-line)] px-5 py-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={showLoading}
              className="flex-1 rounded-xl border border-[var(--dash-line)] bg-white py-2.5 text-sm font-semibold text-[var(--dash-ink)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!hasChange || showLoading}
              className="dash-btn-gradient inline-flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save content
            </button>
          </div>
        </div>
      </div>

      <GalleryPickerModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={handleGallerySelect}
      />
    </>,
    document.body,
  );
}
